import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/ui/Header';
import Icon from '../../../components/AppIcon';

const UserManagement = () => {
  const navigate = useNavigate();
  const [user] = useState({
    id: 'admin-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@moneytransfer.com',
    role: 'admin'
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Mock users data
  const [users, setUsers] = useState([
    {
      id: 'USER-001',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@moneytransfer.com',
      role: 'Super Admin',
      status: 'active',
      lastLogin: new Date(Date.now() - 3600000),
      department: 'Compliance',
      permissions: ['all']
    },
    {
      id: 'USER-002',
      name: 'Michael Chen',
      email: 'michael.chen@moneytransfer.com',
      role: 'Admin',
      status: 'active',
      lastLogin: new Date(Date.now() - 7200000),
      department: 'Operations',
      permissions: ['review', 'approve', 'reports']
    },
    {
      id: 'USER-003',
      name: 'Emily Rodriguez',
      email: 'emily.rodriguez@moneytransfer.com',
      role: 'Operator',
      status: 'active',
      lastLogin: new Date(Date.now() - 10800000),
      department: 'Customer Service',
      permissions: ['review', 'view']
    },
    {
      id: 'USER-004',
      name: 'David Kim',
      email: 'david.kim@moneytransfer.com',
      role: 'Operator',
      status: 'inactive',
      lastLogin: new Date(Date.now() - 86400000 * 7),
      department: 'Compliance',
      permissions: ['review', 'view']
    },
    {
      id: 'USER-005',
      name: 'Lisa Wang',
      email: 'lisa.wang@moneytransfer.com',
      role: 'Senior Operator',
      status: 'active',
      lastLogin: new Date(Date.now() - 14400000),
      department: 'Risk Management',
      permissions: ['review', 'approve', 'flag']
    }
  ]);

  const handleLogout = () => {
    navigate('/login');
  };

  const handleBack = () => {
    navigate('/admin-dashboard');
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Super Admin':
        return 'bg-error/10 text-error border-error/20';
      case 'Admin':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Senior Operator':
        return 'bg-accent/10 text-accent border-accent/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ?'bg-success/10 text-success border-success/20' :'bg-muted text-muted-foreground border-border';
  };

  const formatLastLogin = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date?.toLocaleDateString();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={handleBack}
              className="flex items-center text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="ml-2">Back to Dashboard</span>
            </button>
            <h1 className="text-3xl font-bold text-foreground">User Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage admin users, roles, and permissions
            </p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            <Icon name="UserPlus" size={20} />
            <span>Add New User</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="senior_operator">Senior Operator</option>
                <option value="operator">Operator</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search
              </label>
              <div className="relative">
                <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users?.map((userData) => (
                  <tr key={userData?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-medium">
                            {userData?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                          </span>
                        </div>
                        <div className="ml-4">
                          <p className="font-medium text-foreground">{userData?.name}</p>
                          <p className="text-sm text-muted-foreground">{userData?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(userData?.role)}`}>
                        {userData?.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {userData?.department}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(userData?.status)}`}>
                        {userData?.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatLastLogin(userData?.lastLogin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                          <Icon name="Edit" size={16} className="text-primary" />
                        </button>
                        <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                          <Icon name="Key" size={16} className="text-accent" />
                        </button>
                        <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                          <Icon name="Trash2" size={16} className="text-error" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserManagement;